import { AxiosResponse } from 'axios';
import axiosClient from '../clients/axios-client';
import {
	Company,
	CompanyUpdateDTO,
	CompanyUpdateProjectTypesDTO,
	CompleteCompanySetupDTO,
	CompanyUpdateProjectFoldersDTO,
} from '../data/models/company';
import { GoogleUser, User, UsersFilter } from '../data/models/user';
import { stringifyReqObj } from '../utils/common-utils';
import { Template } from '../data/models/template';

export async function fetchCompany(): Promise<Company> {
	const graphqlQuery = `
            {
                company{
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.company;
}

export async function updateCompany(
	company: CompanyUpdateDTO
): Promise<Company> {
	const graphqlQuery = `
            mutation {
                updateCompany(
                    data: ${stringifyReqObj(company)},
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateCompany;
}

export async function updateQBInfoSheet(googleId: string): Promise<Company> {
	const graphqlQuery = `
            mutation {
                updateQBInfoSheet(
                    googleId: "${googleId}",
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateQBInfoSheet;
}

export async function fetchGoogleUsers(): Promise<GoogleUser[]> {
	const graphqlQuery = `
            {
                googleUsers{
                    ${googleUsersROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.googleUsers;
}

export async function updateCompanyProjectTypes(
	data: CompanyUpdateProjectTypesDTO[]
): Promise<Company> {
	const graphqlQuery = `
            mutation {
                updateCompanyProjectTypes(
                    data: ${stringifyReqObj(data)},
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateCompanyProjectTypes;
}

export async function createCompanyProjectType(data: {
	display: string;
	color?: string;
	textColor?: string;
}): Promise<Company> {
	const graphqlQuery = `
            mutation {
                createCompanyProjectType(
                    data: ${stringifyReqObj(data)},
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.createCompanyProjectType;
}

export async function updateCompanyProjectType(data: {
	id: string;
	active: boolean;
}): Promise<Company> {
	const graphqlQuery = `
            mutation {
                updateCompanyProjectType(
                    data: ${stringifyReqObj(data)},
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateCompanyProjectType;
}

export async function completeCompanySetup(
	data: CompleteCompanySetupDTO
): Promise<Company> {
	const graphqlQuery = `
            mutation {
                completeCompanySetup(data: ${stringifyReqObj(data)}) {
                    ${companyROGraphQl}
                }
            }`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.completeCompanySetup;
}

export async function verifyGoogleDriveSetup(): Promise<any> {
	const graphqlQuery = `
            {
                verifyGoogleDriveSetup
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.verifyGoogleDriveSetup;
}

export async function getAvailableGoogleDrives(
	useServiceWorker: Boolean
): Promise<any> {
	const graphqlQuery = `
            {
                getAvailableGoogleDrives(useServiceWorker: ${useServiceWorker}) {
                    sharedDrives {
                        id
                        name
                        type
                    }
                    myDriveFolders {
                        id
                        name
                        type
                    }
                    nextPageToken
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.getAvailableGoogleDrives;
}

export async function setupCompanyGoogleDrive(
	driveId: string,
	newDriveName: string
): Promise<any> {
	const graphqlQuery = `
            mutation {
                setupCompanyGoogleDrive(driveId: "${driveId}", newDriveName: "${newDriveName}")
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.setupCompanyGoogleDrive;
}

export async function updateCompanyProjectFolders(
	data: CompanyUpdateProjectFoldersDTO[]
): Promise<Company> {
	const graphqlQuery = `
            mutation {
                updateCompanyProjectFolders(
                    data: ${stringifyReqObj(data)},
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateCompanyProjectFolders;
}

export async function createCompanyProjectFolder(data: {
	display: string;
	parentFolderId?: string | null;
}): Promise<Company> {
	const graphqlQuery = `
            mutation {
                createCompanyProjectFolder(
                    data: ${stringifyReqObj(data)},
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.createCompanyProjectFolder;
}

export async function updateCompanyProjectFolder(data: {
	id: string;
	active?: boolean;
	display?: string;
	parentFolderId?: string | null;
}): Promise<Company> {
	const graphqlQuery = `
            mutation {
                updateCompanyProjectFolder(
                    data: ${stringifyReqObj(data)},
                ) {
                    ${companyROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateCompanyProjectFolder;
}

// export async function fetchCompanyTemplates(): Promise<{
// 	list: Template[];
// 	count: number;
// }> {
// 	const graphqlQuery = `
//             {
//                 companyTemplates {
//                     ${companyTemplatesROGraphQl}
//                 }
//             }
//         `;

// 	const response: AxiosResponse = await axiosClient({
// 		url: 'graphql',
// 		method: `POST`,
// 		data: {
// 			query: graphqlQuery,
// 		},
// 	});
// 	return response?.data?.templates;
// }

// const companyTemplatesROGraphQl = `
//     list {
//         id
//         name
//         description
//         created
//         updated
//         mimeType
//         fileSize
//         versions {
//             version
//             googleId
//         }
//         activeVersion {
//             version
//             googleId
//         }
//         active
//     }
//     count
// `;

export const companyROGraphQl = `
    id
    googleDriveSetupVerified
    accountType
    name
    status
    logoUrl {
        url
        signedUrl
    }
    quickLinks {
        id
        title
        url
        icon
        order
    }
    projectTypes {
        id
        display
        color
        textColor
        active
        createdBy {
            id
            name
        }
        created
        updated
    }
    projectFolders {
        id
        display
        active
        createdBy {
            id
            name
        }
        created
        updated
        subFolders {
            id
            display
            active
            createdBy {
                id
                name
            }
            created
            updated
            subFolders {
                id
                display
                active
                createdBy {
                    id
                    name
                }
                created
                updated
            }
        }
    }
    subscription {
        connectedBy {
            id
            name
        }
        trialStart
        trialEnd
        subscriptionId
        customerId
        status
        subscriptionStartDate
        subscriptionEndDate
    }
    quickBooksTimeConnection {
        lastFetchedRecordsFromDb
        failure
        lastFailureReason
        lastFailureDate
        qbInfoSheets {
            key
            value {
                googleFileId
                qbProjectName
                lastUpdated
                failure
                failureReason
                sheetName
            }
        }
    }
    googleDrive {
        driveId
        currentProjectsFolderId
        templatesFolderId
        projectsArchiveFolderId
    }
`;

const googleUsersROGraphQl = `
    name {
        givenName
        familyName
    }
    primaryEmail
    creationTime
    id
`;
